import './EventTile.scss';

import { Trans, useTranslation } from 'react-i18next';

import { ReactComponent as ArrowUpIcon } from 'images/icons/ArrowUp.svg';
import BadgeOld from 'components/BadgeOld/BadgeOld';
import { ReactComponent as CrownIcon } from 'images/icons/Crown.svg';
import { DefaultRoutes } from 'Routes';
import TileOverlay from 'components/TileOverlay/TileOverlay';
import { TrackmanEventModel } from 'types/trackmanEvents';
import VideoThumbnail from 'components/VideoThumbnail/VideoThumbnail';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import dayjs from 'dayjs';
import { formatTimeLength } from 'utilities/formatDate';
import timezone from 'dayjs/plugin/timezone';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

type Props = {
  eventItem: TrackmanEventModel;
  isUserMember?: boolean;
  setIsVideoModalOn?: (isOpen: boolean) => void;
  setVideoModalData?: (data: { videoId: string; videoLabel: string }) => void;
};

export const EventTile = ({ eventItem, isUserMember, setIsVideoModalOn, setVideoModalData }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isPremiumLocked = useMemo(() => eventItem.premiumEvent && !isUserMember, [eventItem.premiumEvent, isUserMember]);

  const overlayText = (
    <Trans i18nKey='PremiumEventOverlayInfo' ns='trackmanEvents'>
      Only <strong>Premium</strong> members can view this Event.&nbsp;
      <span className='learn-more' onClick={() => navigate(DefaultRoutes.Certifications)}>
        Learn more
      </span>
      about upgrading.
    </Trans>
  );

  const goToEventPageHandle = useCallback((url: string): void => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) {
      newWindow.opener = null;
    }
  }, []);

  const handleVideoModalClick = useCallback(() => {
    eventItem.videoId && setIsVideoModalOn && setIsVideoModalOn(true);
    eventItem.videoId && setVideoModalData && setVideoModalData({ videoId: eventItem.videoId, videoLabel: eventItem.title });
  }, [eventItem, setIsVideoModalOn, setVideoModalData]);

  const timeInfo = useMemo(
    () =>
      eventItem.startDate && !eventItem.recordingReady
        ? dayjs(eventItem.startDate).tz(dayjs.tz.guess()).format('MMM DD, h:mma z')
        : dayjs(eventItem.startDate).tz(dayjs.tz.guess()).format('DD/MM/YYYY'),
    [eventItem.startDate, eventItem.recordingReady, t]
  );

  return (
    <div className='event-tile' onClick={() => !isPremiumLocked && handleVideoModalClick()}>
      <button
        className='event-tile-button'
        onClick={() =>
          !eventItem.videoId && !isPremiumLocked ? goToEventPageHandle(eventItem.videoUrl ? eventItem.videoUrl : eventItem.meetingUrl) : {}
        }
      >
        <div className='event-media'>
          {isPremiumLocked ? <TileOverlay color={'#EC691A'} overlayText={overlayText} /> : null}
          {eventItem.videoId ? (
            <VideoThumbnail
              video={eventItem.videoId}
              thumbnailSize='hqdefault'
              badgeText={eventItem.premiumEvent && !isPremiumLocked ? t('Premium', { ns: 'trackmanEvents' }) : ''}
              badgeIcon={eventItem.premiumEvent && !isPremiumLocked ? <CrownIcon /> : null}
            />
          ) : (
            <div className='image-wrapper'>
              <img src={eventItem.imageUrl ?? ''} alt={eventItem.title} />
              {eventItem.premiumEvent && !isPremiumLocked ? <BadgeOld badgeText={t('Premium', { ns: 'trackmanEvents' })} badgeIcon={<CrownIcon />} /> : null}
            </div>
          )}
        </div>
        <div className='event-info'>
          {!eventItem.recordingReady ? <h3>{eventItem.locationType === 'Online' ? t('Online', { ns: 'trackmanEvents' }) : eventItem.location}</h3> : null}
          <div className='time'>
            {timeInfo}
            {eventItem.recordingReady && eventItem.videoLength ? (
              <span className='video-length'>{formatTimeLength(eventItem.videoLength)}</span>
            ) : null}
          </div>
          <div className='event-title'>
            {eventItem.title}
            {!eventItem.recordingReady ? <ArrowUpIcon /> : null}
          </div>
        </div>
      </button>
    </div>
  );
};
